import imageCompression from 'browser-image-compression';
import { CompressionResult } from '../types';

export interface CompressionOptions {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  useWebWorker: boolean;
  quality: number;
  initialQuality: number;
  alwaysKeepResolution: boolean;
  fileType?: string;
}

export class IOSSafariImageCompressor {
  private static getDefaultOptions(isIOSSafari: boolean): CompressionOptions {
    return {
      maxSizeMB: isIOSSafari ? 5 : 10, // Smaller files for iOS Safari
      maxWidthOrHeight: isIOSSafari ? 2048 : 4096,
      useWebWorker: false, // Disable web workers for iOS Safari compatibility
      quality: isIOSSafari ? 0.7 : 0.8,
      initialQuality: isIOSSafari ? 0.8 : 0.9,
      alwaysKeepResolution: false,
      fileType: 'image/jpeg', // Always convert to JPEG for compatibility
    };
  }

  static async compressImage(
    file: File,
    isIOSSafari: boolean = false,
    customOptions?: Partial<CompressionOptions>
  ): Promise<CompressionResult> {
    const startTime = performance.now();
    const originalSize = file.size;

    console.log(`🗜️ Starting compression for ${file.name} (${Math.round(originalSize / 1024)}KB)`);
    
    if (isIOSSafari) {
      console.log('📱 Using iOS Safari optimized compression settings');
    }

    const options = {
      ...this.getDefaultOptions(isIOSSafari),
      ...customOptions,
    };

    try {
      // Smart skip: Don't compress files that are already optimally sized
      const sizeMB = originalSize / 1024 / 1024;
      const isAlreadyJPEG = file.type === 'image/jpeg' || file.type === 'image/jpg';
      
      // Skip if file is small JPEG (< 2MB) or already under target size
      if ((sizeMB < 2 && isAlreadyJPEG) || (sizeMB <= options.maxSizeMB && this.isCompatibleFormat(file.type))) {
        console.log(`✅ Skipping compression for ${file.name} (${sizeMB.toFixed(1)}MB, already optimal)`);
        return {
          compressedFile: file,
          originalSize,
          compressedSize: originalSize,
          compressionRatio: 1,
          processingTime: performance.now() - startTime,
        };
      }

      // Handle HEIC files specially for iOS Safari
      if (file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().includes('.heic')) {
        console.log('📱 HEIC file detected, using special handling for iOS Safari');
        return await this.compressHEICFile(file, options, startTime, originalSize);
      }

      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: options.maxSizeMB,
        maxWidthOrHeight: options.maxWidthOrHeight,
        useWebWorker: options.useWebWorker,
        initialQuality: options.quality,
        alwaysKeepResolution: options.alwaysKeepResolution,
        fileType: options.fileType,
      });

      const compressedSize = compressedFile.size;
      const compressionRatio = originalSize / compressedSize;
      const processingTime = performance.now() - startTime;

      console.log(`✅ Compression complete for ${file.name}:`);
      console.log(`  - Original: ${Math.round(originalSize / 1024)}KB`);
      console.log(`  - Compressed: ${Math.round(compressedSize / 1024)}KB`);
      console.log(`  - Ratio: ${compressionRatio.toFixed(2)}x`);
      console.log(`  - Time: ${Math.round(processingTime)}ms`);

      return {
        compressedFile,
        originalSize,
        compressedSize,
        compressionRatio,
        processingTime,
      };

    } catch (error) {
      console.error(`❌ Compression failed for ${file.name}:`, error);
      
      // Fallback: return original file if compression fails
      console.log(`⚠️ Using original file as fallback for ${file.name}`);
      return {
        compressedFile: file,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 1,
        processingTime: performance.now() - startTime,
      };
    }
  }

  private static async compressHEICFile(
    file: File,
    options: CompressionOptions,
    startTime: number,
    originalSize: number
  ): Promise<CompressionResult> {
    try {
      // For HEIC files, we need to convert them to JPEG
      console.log('🔄 Converting HEIC to JPEG...');
      
      const compressedFile = await imageCompression(file, {
        maxSizeMB: options.maxSizeMB,
        maxWidthOrHeight: options.maxWidthOrHeight,
        useWebWorker: false, // Always disabled for HEIC
        initialQuality: 0.8,
        fileType: 'image/jpeg', // Force JPEG output
      });

      // Rename the file to have .jpg extension
      const newFile = new File([compressedFile], 
        file.name.replace(/\.(heic|heif)$/i, '.jpg'), 
        { type: 'image/jpeg' }
      );

      const compressedSize = newFile.size;
      const compressionRatio = originalSize / compressedSize;
      const processingTime = performance.now() - startTime;

      console.log(`✅ HEIC conversion complete: ${file.name} → ${newFile.name}`);
      console.log(`  - Original HEIC: ${Math.round(originalSize / 1024)}KB`);
      console.log(`  - Converted JPEG: ${Math.round(compressedSize / 1024)}KB`);

      return {
        compressedFile: newFile,
        originalSize,
        compressedSize,
        compressionRatio,
        processingTime,
      };

    } catch (error) {
      console.error('❌ HEIC conversion failed:', error);
      throw error;
    }
  }

  private static isCompatibleFormat(mimeType: string): boolean {
    const compatibleTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];
    return compatibleTypes.includes(mimeType);
  }

  static async compressBatch(
    files: File[],
    isIOSSafari: boolean = false,
    onProgress?: (progress: number, currentFile: string) => void
  ): Promise<CompressionResult[]> {
    console.log(`🗜️ Starting batch compression of ${files.length} files`);
    
    const results: CompressionResult[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (onProgress) {
        onProgress((i / files.length) * 100, file.name);
      }

      try {
        const result = await this.compressImage(file, isIOSSafari);
        results.push(result);

        // Minimal delay between compressions to prevent blocking UI
        if (isIOSSafari && i < files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 10)); // Reduced from 100ms
        }

      } catch (error) {
        console.error(`❌ Failed to compress ${file.name}:`, error);
        
        // Add failed result
        results.push({
          compressedFile: file,
          originalSize: file.size,
          compressedSize: file.size,
          compressionRatio: 1,
          processingTime: 0,
        });
      }
    }

    if (onProgress) {
      onProgress(100, 'Complete');
    }

    const totalOriginal = results.reduce((sum, r) => sum + r.originalSize, 0);
    const totalCompressed = results.reduce((sum, r) => sum + r.compressedSize, 0);
    const overallRatio = totalOriginal / totalCompressed;

    console.log(`✅ Batch compression complete:`);
    console.log(`  - Files processed: ${results.length}`);
    console.log(`  - Total original size: ${Math.round(totalOriginal / 1024 / 1024)}MB`);
    console.log(`  - Total compressed size: ${Math.round(totalCompressed / 1024 / 1024)}MB`);
    console.log(`  - Overall compression ratio: ${overallRatio.toFixed(2)}x`);

    return results;
  }

  static getMemoryUsage(): { used: number; limit: number } | null {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        used: memory.usedJSHeapSize,
        limit: memory.jsHeapSizeLimit,
      };
    }
    return null;
  }

  static checkMemoryBeforeCompression(file: File): boolean {
    const memory = this.getMemoryUsage();
    if (!memory) return true; // Can't check, assume it's okay

    const estimatedMemoryNeeded = file.size * 3; // Rough estimate
    const availableMemory = memory.limit - memory.used;

    if (estimatedMemoryNeeded > availableMemory * 0.5) {
      console.warn(`⚠️ Insufficient memory for compression: need ~${Math.round(estimatedMemoryNeeded / 1024 / 1024)}MB, available ~${Math.round(availableMemory / 1024 / 1024)}MB`);
      return false;
    }

    return true;
  }
}
